// Stripe payment-link panel for delivery and takeaway orders — React port of pos_web's
// stripe-payment-link.php Alpine component. Only rendered for those order types once
// "ONLINE PAYMENT" is selected (same gating pos_web uses).
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';

const StripePaymentLinkPanel = ({ orderId, orderTypeNum, paymentMethod, PAYMENT_MODES }) => {
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const pollRef = useRef(null);
  const redirectedRef = useRef(false);

  // matches common\models\Order::ORDER_TYPE['take_away']=1 / ['delivery']=2
  const isDeliveryOrTakeaway = Number(orderTypeNum) === 1 || Number(orderTypeNum) === 2;
  const isOnlineSelected = paymentMethod === PAYMENT_MODES.online;
  const visible = isDeliveryOrTakeaway && isOnlineSelected;

  const fetchStatusOnce = async () => {
    if (!orderId) return;
    try {
      const res = await callAPI(apiUrls.PAYMENT_LINK_STATUS, { order_id: orderId }, 'GET', null, {}, {}, false);
      setLink(res?.data?.data || null);
    } catch (err) {
      console.error('payment-link-status error', err);
    }
  };

  // Check once (plain REST, no live connection) whether a link already exists for this
  // order — covers a page reload after a link was already generated earlier.
  useEffect(() => {
    if (!visible || !orderId) return;
    fetchStatusOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, orderId]);

  // Only poll once a payment link actually exists for this order — no need to poll while
  // staff hasn't even clicked "Generate Payment Link" yet — and stop once it's paid.
  useEffect(() => {
    if (!visible || !link || !orderId || link.status === 'paid') {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    pollRef.current = setInterval(fetchStatusOnce, 5000);

    return () => {
      clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, link?.id, link?.status, orderId]);

  useEffect(() => {
    if (link && link.status === 'paid' && !redirectedRef.current) {
      redirectedRef.current = true;
      setTimeout(() => {
        navigate('/dashboard');
      }, 2500);
    }
  }, [link, navigate]);

  if (!visible) {
    return null;
  }

  const generate = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const res = await callAPI(apiUrls.CREATE_STRIPE_PAYMENT_LINK, {}, 'POST', { order_id: orderId });
      if (res?.data?.status === 1) {
        await fetchStatusOnce();
      } else {
        setErrorMessage(res?.data?.message || 'Could not generate payment link.');
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || 'Could not generate payment link.');
    } finally {
      setLoading(false);
    }
  };

  // Calls /v3/cart/payment directly (same endpoint the normal Pay/Unpaid buttons use),
  // instead of routing through CheckoutSummary's own handlePayment() — with payment_mode
  // already "online", that flow checks for a redirect_url and would send staff to a card
  // entry screen instead of back to the dashboard. Backend handles the actual finalize
  // (surcharges, gift vouchers, etc.), same as any other payment method.
  const finalizeAsUnpaid = async () => {
    try {
      await callAPI(apiUrls.ORDER_PAYMENT, {}, 'POST', {
        payment_mode: PAYMENT_MODES.online,
        order_id: orderId,
        paid: 0,
        suppress_print: 1,
      });
    } catch (err) {
      console.error('finalizeAsUnpaid error', err);
    } finally {
      navigate('/dashboard');
    }
  };

  const send = async () => {
    if (!link) return;
    setSending(true);
    setErrorMessage('');
    try {
      const res = await callAPI(apiUrls.SEND_STRIPE_PAYMENT_LINK, {}, 'POST', { payment_link_id: link.id });
      if (res?.data?.status === 1) {
        await fetchStatusOnce();
        toast.success('Payment link sent via SMS.');
        finalizeAsUnpaid();
      } else {
        setErrorMessage(res?.data?.message || 'Could not send payment link.');
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || 'Could not send payment link.');
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.short_url);
      toast.success('Link copied to clipboard.');
    } catch (err) {
      console.error('clipboard error', err);
    }

    const markUnpaidAndGo = window.confirm(
      'Link copied.\n\n' +
      'Do you want to mark this order as unpaid and start a new order?\n\n' +
      "Click OK to mark unpaid and go to the dashboard — you'll be notified automatically " +
      'once the customer completes payment.\n\n' +
      'Click Cancel to keep waiting here for the payment instead.'
    );

    if (markUnpaidAndGo) {
      finalizeAsUnpaid();
    }
  };

  return (
    <div className="cardStyle mt-3 p-3">
      <h4 className="smTitle mb-2">Send Stripe Payment Link</h4>

      {!link && (
        <div>
          <p className="text-muted small">Generate a secure Stripe payment link and text it to the customer for this order.</p>
          <button type="button" className="btn btn-primary" disabled={loading} onClick={generate}>
            {loading ? 'Generating...' : 'Generate Payment Link'}
          </button>
        </div>
      )}

      {link && link.status === 'paid' && (
        <p className="text-success"><strong>Payment received. This order is now paid.</strong></p>
      )}

      {link && link.status !== 'paid' && (
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-primary" disabled={sending} onClick={send}>
            {sending ? 'Sending...' : (link.status === 'created' ? 'Send via SMS' : 'Resend via SMS')}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={copyLink}>
            Copy Link
          </button>
        </div>
      )}

      {errorMessage && <p className="text-danger small mt-2">{errorMessage}</p>}
    </div>
  );
};

export default StripePaymentLinkPanel;
